import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';

/**
 * Company-scoped reference lists for the entry forms.
 *
 * These change rarely, so they are cached for five minutes and shared across
 * every drawer rather than refetched each time one opens.
 */

export interface Option { id: string; name: string }
export interface ProjectOption extends Option { value_paise?: number; client_id?: string }
export interface UnitOption extends Option { short_name: string }

function list<T>(rows: T[] | null, error: { message: string } | null): T[] {
  if (error) throw new Error(error.message);
  return rows ?? [];
}

const FIVE_SECONDS = 5_000;

export function useLookups() {
  return useQuery({
    queryKey: ['lookups'],
    staleTime: FIVE_SECONDS,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const [materials, suppliers, subs, workTypes, units, expenseCats, projects, clients] =
        await Promise.all([
          supabase.from('materials')
            .select('id, name, unit_id, rate_paise').is('deleted_at', null).order('name'),
          supabase.from('suppliers')
            .select('id, name').is('deleted_at', null).order('name'),
          supabase.from('subcontractors')
            .select('id, name').is('deleted_at', null).order('name'),
          supabase.from('work_types')
            .select('id, name').eq('is_active', true).order('sort_order'),
          supabase.from('units')
            .select('id, name, short_name').eq('is_active', true).order('sort_order'),
          supabase.from('expense_categories')
            .select('id, name').eq('is_active', true).order('sort_order'),
          supabase.from('projects')
            .select('id, name, value_paise, client_id').is('deleted_at', null).order('name'),
          supabase.from('clients')
            .select('id, name').is('deleted_at', null).order('name'),
        ]);

      return {
        materials: list(materials.data, materials.error),
        suppliers: list(suppliers.data, suppliers.error) as Option[],
        subcontractors: list(subs.data, subs.error) as Option[],
        workTypes: list(workTypes.data, workTypes.error) as Option[],
        units: list(units.data, units.error) as UnitOption[],
        expenseCategories: list(expenseCats.data, expenseCats.error) as Option[],
        projects: list(projects.data, projects.error) as ProjectOption[],
        clients: list(clients.data, clients.error) as Option[],
      };
    },
  });
}

export const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'neft', label: 'NEFT' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'imps', label: 'IMPS' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
] as const;

export const CLIENT_PAYMENT_TYPES = [
  { value: 'advance', label: 'Advance' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'final', label: 'Final' },
  { value: 'retention_release', label: 'Retention release' },
  { value: 'other', label: 'Other' },
] as const;

/** Today as YYYY-MM-DD in local time — never toISOString(), which is UTC and
 *  silently records yesterday for anyone east of Greenwich after 05:30 IST. */
export function todayLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export const STANDARD_MATERIAL_UNITS = [
  'Bags',
  'm³',
  'cft',
  'Brass',
  'kg',
  'Quintals',
  'Tonnes',
  'Nos',
  'm²',
  'Litres',
  'Linear metres',
  'boxes',
  'Sq.ft',
  'cm',
] as const;

export type StandardMaterialUnit = (typeof STANDARD_MATERIAL_UNITS)[number];

export function normalizeUnitLabel(name?: string | null, shortName?: string | null): string {
  if (!name && !shortName) return '';
  const raw = `${name ?? ''} ${shortName ?? ''}`.toLowerCase();
  if (raw.includes('bag')) return 'Bags';
  if (raw.includes('tonne') || raw.includes('ton')) return 'Tonnes';
  if (raw.includes('cu.m') || raw.includes('cum') || raw.includes('m³') || raw.includes('cubic m')) return 'm³';
  if (raw.includes('cu.ft') || raw.includes('cft') || raw.includes('cubic ft') || raw.includes('units (100') || raw.includes('units')) return 'cft';
  if (raw.includes('sq.ft') || raw.includes('sqft') || raw.includes('square ft')) return 'Sq.ft';
  if (raw.includes('sq.m') || raw.includes('m²') || raw.includes('sqm') || raw.includes('square m')) return 'm²';
  if (raw.includes('brass')) return 'Brass';
  if (raw.includes('quintal')) return 'Quintals';
  if (raw.includes('kg') || raw.includes('kilogram')) return 'kg';
  if (raw.includes('litre') || raw.includes('liter')) return 'Litres';
  if (raw.includes('box')) return 'boxes';
  if (raw.includes('length') || raw.includes('linear') || raw.includes('running m') || raw.includes('rm')) return 'Linear metres';
  if (raw.includes('cm') || raw.includes('centimetre') || raw.includes('centimeter')) return 'cm';
  if (raw.includes('no') || raw.includes('piece') || raw.includes('item') || raw.includes('number')) return 'Nos';
  return name?.trim() || shortName?.trim() || '';
}

/**
 * Clean dropdown options for material units:
 * Bags, m³, cft, Brass, kg, Quintals, Tonnes, Nos, m², Litres, Linear metres, boxes, Sq.ft, cm
 */
export function getStandardMaterialUnitOptions(dbUnits?: Array<{ id: string; name: string; short_name?: string }>) {
  const map = new Map<string, string>();
  for (const u of dbUnits ?? []) {
    const label = normalizeUnitLabel(u.name, u.short_name);
    if (!map.has(label)) {
      map.set(label, u.id);
    }
  }

  const options: Array<{ value: string; label: string }> = [];
  for (const std of STANDARD_MATERIAL_UNITS) {
    const unitId = map.get(std) || `__std__${std}`;
    options.push({ value: unitId, label: std });
  }

  // Include any other custom units that aren't in the standard 14
  for (const u of dbUnits ?? []) {
    const label = normalizeUnitLabel(u.name, u.short_name);
    if (!STANDARD_MATERIAL_UNITS.includes(label as any) && !options.some((o) => o.value === u.id)) {
      options.push({ value: u.id, label });
    }
  }

  return options;
}

